import React, { useState, useEffect, useRef } from 'react';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/hooks/use-toast';
import { sendContactMessage, subscribeToUserMessages, sendReply, closeMailThread, type Mail } from '@/services/firebaseService';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from '@/components/ui/card';
import { Mail as MailIcon, Send, Info, MessageSquare, Plus, Clock, User, UserCircle, XCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Label } from '@/components/ui/label';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '@/components/ui/select';


interface Message {
  id: string;
  text: string;
  sender: 'user' | 'support';
  timestamp: Date;
  read: boolean;
  deliveryMessageId?: string;
  references?: string;
}

interface ChatSession {
  id: string;
  subject: string;
  lastMessage: string;
  lastTimestamp: Date;
  messages: Message[];
  status: string;
}

const ContactCoach = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { user } = useAuth();
  
  // Chat state
  const [chats, setChats] = useState<ChatSession[]>([]);
  const [activeChat, setActiveChat] = useState<ChatSession | null>(null);
  const [replyMessage, setReplyMessage] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('');
  const [isCloseConfirmOpen, setIsCloseConfirmOpen] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const [formData, setFormData] = useState<{
    name: string;
    email: string;
    subject: string;
    message: string;
    userId: string | null;
  }>({
    name: '',
    email: '',
    subject: '',
    message: '',
    userId: null
  });

  useEffect(() => {
  if (user) {
    console.log(user);
    setFormData(prev => ({
      ...prev,
      name: user.firstname + ' ' + user.lastname,
      email: user.email,
      userId: user.userId,
      subject: '',
      message: ''
    }));
  }
}, [user]);

  useEffect(() => {
    if (!user?.userId) return;

    const unsubscribe = subscribeToUserMessages(user.userId, (mails) => {
      const sessionsMap: Record<string, ChatSession> = {};
      const orphanMessages: Mail[] = [];

      // 1. Identify threads
      mails.forEach(mail => {
        if (mail.firstMessage) {
          sessionsMap[mail.id] = {
            id: mail.id,
            subject: mail.message.subject,
            lastMessage: mail.message.text || mail.message.html.replace(/<[^>]*>/g, '').trim() || '',
            lastTimestamp: mail.delivery?.startTime?.toDate() || new Date(),
            status: mail.status || 'pending',
            messages: []
          };
        } else {
          orphanMessages.push(mail);
        }
      });

      // Helper to convert
      const convertToMessage = (mail: Mail): Message => {
        // We assume messages where 'from' matches user email are from 'user', others from 'support'
        const isMe = mail.from === user.email;
        return {
          id: mail.id,
          text: mail.message.text || mail.message.html.replace(/<[^>]*>/g, '').trim(),
          sender: isMe ? 'user' : 'support',
          timestamp: mail.delivery?.startTime?.toDate() || new Date(),
          read: true,
          deliveryMessageId: mail.delivery?.info?.messageId,
          references: mail.message.headers?.find(h => h.key === 'References')?.value
        };
      };

      // 2. Populate roots
      Object.values(sessionsMap).forEach(session => {
        const rootMail = mails.find(m => m.id === session.id);
        if (rootMail) session.messages.push(convertToMessage(rootMail));
      });

      // 3. Attach replies
      orphanMessages.forEach(mail => {
        const threadId = mail.originId;
        if (threadId && sessionsMap[threadId]) {
          sessionsMap[threadId].messages.push(convertToMessage(mail));
          const msgDate = mail.delivery?.startTime?.toDate() || new Date();
          if (msgDate > sessionsMap[threadId].lastTimestamp) {
            sessionsMap[threadId].lastTimestamp = msgDate;
            sessionsMap[threadId].lastMessage = mail.message.text || mail.message.html.replace(/<[^>]*>/g, '').trim() || "Nouveau message";
          }
        }
      });

      // 4. Sort messages
      Object.values(sessionsMap).forEach(session => {
        session.messages.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
      });

      // 5. Sort chats
      const sortedChats = Object.values(sessionsMap).sort((a, b) => b.lastTimestamp.getTime() - a.lastTimestamp.getTime());
      setChats(sortedChats);

      // Automatically select the first open chat if none is active
      setActiveChat(prevActiveChat => {
        // If no chat was active, find the first open one
        if (!prevActiveChat) {
          const firstOpenChat = sortedChats.find(chat => chat.status !== 'closed');
          return firstOpenChat || null;
        }

        // If a chat was active, try to find its updated version
        const updatedActiveChat = sortedChats.find(c => c.id === prevActiveChat.id);
        if (updatedActiveChat) {
          return updatedActiveChat;
        }

        // If the previously active chat is gone (e.g., closed), find the next available open one
        const nextOpenChat = sortedChats.find(chat => chat.status !== 'closed');
        return nextOpenChat || null;
      });
    });

    return () => unsubscribe();
  }, [user?.userId]);

  // Auto-scroll to bottom of chat
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [activeChat?.messages]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const finalSubject = selectedSubject === 'other' ? formData.subject : selectedSubject;
    if (!formData.name || !formData.email || !finalSubject || !formData.message) {
      toast({
        title: "Erreur",
        description: t('contact.form.error'),
        variant: "destructive",
      });
      return;
    }
    setIsSubmitting(true);
    
    try {
      await sendContactMessage({
        name: formData.name,
        email: formData.email,
        subject: finalSubject,
        message: formData.message,
        userId: formData.userId,
      });

      toast({
        title: t('contact.form.thankYou'),
        description: t('contact.form.success'),
        variant: "default",
      });

      setFormData({
        name: user && user.firstname + ' ' + user.lastname || '',
        email: user && user.email || '',
        subject: '',
        message: '',
        userId: user && user.userId || null
      });
      setSelectedSubject('');
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Une erreur est survenue lors de l'envoi du message.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSendReply = async () => {
    if (!replyMessage.trim() || !activeChat) return;
    
    try {
      const lastMsg = activeChat.messages[activeChat.messages.length - 1];
      const previousMessageId = lastMsg?.deliveryMessageId || '';
      const previousReferences = lastMsg?.references || '';
      const newReferences = `${previousReferences} ${previousMessageId}`.trim();
      
      const subject = activeChat.subject.startsWith('Re:') ? activeChat.subject : `Re: ${activeChat.subject}`;

      await sendReply({
        originId: activeChat.id,
        firstMessage: false,
        type: 'coach',
        from: user.email,
        to: ['jojoka9569@gmail.com'], // Default support email, should match Cloud Function
        userId: user.userId,
        message: {
          subject: subject,
          html: `<p>${replyMessage}</p>`,
          text: replyMessage,
          headers: [
            { key: "In-Reply-To", value: previousMessageId },
            { key: "References", value: newReferences }
          ]
        }
      });
      setReplyMessage('');
    } catch (error) {
      console.error("Failed to reply", error);
      toast({
        title: "Erreur",
        description: "Impossible d'envoyer la réponse",
        variant: "destructive"
      });
    }
  };

  const handleCloseConversation = async () => {
    if (!activeChat) return;
    try {
      await closeMailThread(activeChat.id);
      setActiveChat(null); // Go back to the list view
      toast({
        title: "Conversation closed",
        description: "This conversation has been closed.",
      });
    } catch (error) {
      console.error("Failed to close conversation", error);
      toast({
        title: "Error",
        description: "Could not close the conversation.",
        variant: "destructive",
      });
    } finally {
      setIsCloseConfirmOpen(false);
    }
  };

  const formatMessageTime = (date: Date) => {
    return date ? format(date, 'HH:mm') : '';
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Navbar />
      
      <main className="flex-grow pt-8">
        <section className="relative py-16 overflow-hidden">
          <div className="container">
          <Card className="bg-transparent shadow-none border-none md:bg-white md:ring-1 md:ring-gray-200 md:shadow-sm md:rounded-2xl">
          <CardHeader className="hidden md:block">
            <div className="flex justify-between items-center mb-1">
              <CardTitle>{t('nav.support')}</CardTitle>
            </div>
          </CardHeader>
          <div className="absolute inset-0 bg-gradient-to-b from-blue-50 via-white to-purple-50 -z-10" />
          <div className="absolute top-40 right-10 w-72 h-72 rounded-full bg-blue-300/10 filter blur-3xl -z-10" />
          <div className="absolute bottom-10 left-10 w-72 h-72 rounded-full bg-purple-300/10 filter blur-3xl -z-10" />

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 max-w-7xl mx-auto pb-8">
              <div className="lg:col-span-1 space-y-8 animated-element appear">
                {chats.length > 0 ? (
                  // List of messages if user has any history
                  <Card className="h-[600px] flex flex-col bg-white/80 backdrop-blur-sm shadow-md border-slate-100">
                    <CardHeader className="pb-2">
                      <div className="flex justify-between items-center">
                        <CardTitle className="text-lg">Messages</CardTitle>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => setActiveChat(null)}
                          className="h-8 w-8 p-0 rounded-full"
                        >
                          <Plus className="h-4 w-4" />
                        </Button>
                      </div>
                    </CardHeader>
                    <ScrollArea className="flex-1">
                      <div className="p-3 space-y-2">
                        {chats.map(chat => (
                          <div
                            key={chat.id}
                            onClick={() => setActiveChat(chat)}
                            className={`p-3 rounded-lg cursor-pointer transition-colors border ${
                              activeChat?.id === chat.id 
                                ? 'bg-blue-50 border-blue-200' 
                                : 'bg-white hover:bg-slate-50 border-transparent hover:border-slate-200'
                            }`}
                          >
                            <div className="flex justify-between items-start mb-1">
                              <h4 className="font-medium text-sm text-slate-900 truncate pr-2 flex-1">{chat.subject}</h4>
                              <span className="text-[10px] text-slate-500 whitespace-nowrap">
                                {format(chat.lastTimestamp, 'dd/MM')}
                              </span>
                            </div>
                            <p className="text-xs text-slate-500 line-clamp-2">{chat.lastMessage}</p>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  </Card>
                ) : (
                  // Info card if no history
                <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-md p-8 border border-slate-100">
                  <h3 className="text-xl font-bold mb-6 text-blue-600">
                    {t('contact.info.title')}
                  </h3>
                  
                  <div className="space-y-6">
                    <div className="flex items-start">
                      <div className="flex-shrink-0 h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center mr-4">
                        <Info className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{t('contact.helpInfo')}</p>
                        <p className="text-sm text-gray-600">{t('contact.info.help')}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start">
                      <div className="flex-shrink-0 h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center mr-4">
                        <MailIcon className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">Email</p>
                        <p className="text-sm text-gray-600">{t('contact.info.email')}</p>
                      </div>
                    </div>
                  </div>
                </div>
                )}
              </div>
              
              <div className="lg:col-span-2 animated-element appear">
                {activeChat ? (
                  // Chat Interface
                  <Card className={`h-[600px] flex flex-col bg-white shadow-md border-slate-100 overflow-hidden ${activeChat.status === 'closed' ? 'opacity-50' : ''}`}>
                    <CardHeader className="border-b py-4 bg-slate-50/50">
                      <div className="flex items-center gap-3">
                        <Avatar className="h-10 w-10 border-2 border-white shadow-sm">
                          <AvatarFallback className="bg-blue-600 text-white">S</AvatarFallback>
                        </Avatar>
                        <div>
                          <CardTitle className="text-base">{activeChat.subject}</CardTitle>
                          <p className="text-xs text-muted-foreground">ThePoppyApp Support</p>
                        </div>
                        {activeChat.status !== 'closed' && (
                          <div className="ml-auto">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setIsCloseConfirmOpen(true)}
                            >
                              <XCircle className="h-5 w-5 text-gray-400 hover:text-red-500" />
                                {t('common.close')}
                            </Button>
                          </div>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-50/30">
                      {activeChat.messages.map((msg) => (
                        <div 
                          key={msg.id} 
                          className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}
                        >
                          <div className={`flex flex-col max-w-[80%] ${msg.sender === 'user' ? 'items-end' : 'items-start'}`}>
                            <div 
                              className={`px-4 py-3 rounded-2xl shadow-sm text-sm ${
                                msg.sender === 'user' 
                                  ? 'bg-blue-600 text-white rounded-tr-none' 
                                  : 'bg-white border border-slate-100 text-slate-800 rounded-tl-none'
                              }`}
                            >
                              {msg.text}
                            </div>
                            <span className="text-[10px] text-slate-400 mt-1 px-1">
                              {formatMessageTime(msg.timestamp)}
                            </span>
                          </div>
                        </div>
                      ))}
                      <div ref={messagesEndRef} />
                    </CardContent>
                    {activeChat.status !== 'closed' ? (
                      <CardFooter className="p-3 border-t bg-white">
                        <div className="flex w-full gap-2 items-end">
                          <Textarea 
                            value={replyMessage}
                            onChange={(e) => setReplyMessage(e.target.value)}
                            placeholder="Écrivez votre réponse..." 
                            className="min-h-[50px] resize-none border-slate-200 focus:border-blue-500 focus:ring-blue-500"
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                handleSendReply();
                              }
                            }}
                          />
                          <Button 
                            onClick={handleSendReply}
                            disabled={!replyMessage.trim()}
                            className="mb-0.5 bg-blue-600 hover:bg-blue-700 h-10 w-10 p-0 rounded-full"
                          >
                            <Send className="h-4 w-4" />
                          </Button>
                        </div>
                      </CardFooter>
                    ) : (
                      <CardFooter className="p-4 border-t bg-gray-50 text-center text-sm text-muted-foreground">
                        {t('contact.contactClosed')}.
                      </CardFooter>
                    )}
                  </Card>
                ) : chats.filter(c => c.status !== 'closed').length > 0 ? (
                  <div className="bg-white rounded-2xl shadow-md p-8 border border-slate-100 h-[600px] flex flex-col items-center justify-center text-center">
                      <MessageSquare className="h-12 w-12 text-gray-300 mb-4" />
                      <h3 className="text-lg font-medium">{('contact.select')}</h3>
                      <p className="text-muted-foreground max-w-sm">{t('contact.selectDesc')}.</p>
                  </div>
                ) : (
                  // Contact Form
                <div className="bg-white rounded-2xl shadow-md p-8 border border-slate-100">
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <label htmlFor="name" className="text-sm font-medium">
                          {t('contact.form.name')} <span className="text-red-500">*</span>
                        </label>
                        <Input
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          placeholder="John Doe"
                          required
                          readOnly
                          className="bg-gray-50 rounded-lg border-slate-200 focus:border-blue-500"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <label htmlFor="email" className="text-sm font-medium">
                          {t('contact.form.email')} <span className="text-red-500">*</span>
                        </label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="john@example.com"
                          required
                          className="rounded-lg border-slate-200 focus:border-blue-500"
                        />
                      </div>
                    </div>
                    
                     <div className="space-y-2">
                      <Label htmlFor="subject-select">{t('contact.form.subject')} <span className="text-red-500">*</span></Label>
                      <Select value={selectedSubject} onValueChange={setSelectedSubject}>
                        <SelectTrigger id="subject-select">
                          <SelectValue placeholder={t('contact.choiceSubject.selectTopic')} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Account Issues">{t('contact.choiceSubject.accountIssues')}</SelectItem>
                          <SelectItem value="Billing & Payments">{t('contact.choiceSubject.billingPayments')}</SelectItem>
                          <SelectItem value="Feature Requests">{t('contact.choiceSubject.featureRequests')}</SelectItem>
                          <SelectItem value="Technical Support">{t('contact.choiceSubject.technicalSUpport')}</SelectItem>
                          <SelectItem value="other">{t('contact.choiceSubject.other')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {selectedSubject === 'other' && (
                      <div className="space-y-2">
                        <Input
                          id="subject"
                          name="subject"
                          value={formData.subject}
                          onChange={handleChange}
                          placeholder={t('contact.choiceSubject.enterTopic')}
                          required
                          className="rounded-lg border-slate-200 focus:border-blue-500"
                        />
                      </div>
                    )}

                    <div className="space-y-2">
                      <label htmlFor="message" className="text-sm font-medium">
                        {t('contact.form.message')} <span className="text-red-500">*</span>
                      </label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        placeholder={t('contact.messagePlaceholder')}
                        required
                        className="rounded-lg min-h-[179px] border-slate-200 focus:border-blue-500"
                      />
                    </div>
                    
                    <Button 
                      type="submit" 
                      className="w-full md:w-auto bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send className="mr-2 h-4 w-4" />
                          {t('common.send')}
                        </>
                      )}
                    </Button>
                  </form>
                </div>
                )}
              </div>
          
          </div>
               </Card>
                  <AlertDialog open={isCloseConfirmOpen} onOpenChange={setIsCloseConfirmOpen}>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>{t('contact.closeContact')}?</AlertDialogTitle>
                        <AlertDialogDescription className='text-red-700'>
                          {t('contact.closeContactDesc')}.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                        <AlertDialogAction onClick={handleCloseConversation}>{t('common.done')}</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                 </div>
        </section>
      
      </main>
      
      <Footer />
    </div>
  );
};

export default ContactCoach;